import { useEffect, useState } from "react";
import { api, API_URL, getToken } from "@/lib/api";
import { useSession } from "@/lib/session";

type AppRow = {
  id: number;
  name: string;
  slug: string;
  status: string;
  is_default: boolean;
};

type PbxFields = {
  mqtt_api_enable: string;
  host: string;
  host_hostname?: string;
  port: string;
  enable_tls: string;
  username: string;
  password: string;
  client_id: string;
  token: string;
  keepalive: string;
  reconnect: string;
};

type MqttGuide = {
  mode: string;
  device_id?: number | null;
  device_name?: string | null;
  connection_status?: string;
  last_seen_at?: string | null;
  server?: {
    vps_ip: string;
    mqtt_host: string;
    mqtt_hostname?: string;
    mqtt_port: number;
    mqtt_tls: boolean;
    cloud_api: string;
  };
  pbx_fields: PbxFields;
  topics?: Record<string, string> | null;
  notes?: string[];
  app?: AppRow;
};

const PBX_FIELDS: Array<[keyof PbxFields, string]> = [
  ["mqtt_api_enable", "MQTT API / Enable"],
  ["host", "Host (VPS IP)"],
  ["host_hostname", "Host (hostname)"],
  ["port", "Port"],
  ["enable_tls", "Enable TLS / SSL"],
  ["username", "Username"],
  ["password", "Password"],
  ["client_id", "Client ID"],
  ["token", "Token / Device token"],
  ["keepalive", "Keep-alive"],
  ["reconnect", "Reconnect"],
];

export default function AppsPage() {
  const { user, org, setApp } = useSession();
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const [appId, setAppId] = useState<number | null>(null);
  const [appName, setAppName] = useState("");
  const [guide, setGuide] = useState<MqttGuide | null>(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const rows = await api<AppRow[]>("/apps");
        const list = Array.isArray(rows) ? rows : [];
        const preferred =
          list.find((a) => String(a.id) === String(org?.id)) ||
          list.find((a) => a.is_default) ||
          list[0];
        if (!preferred) {
          setError("No app for this account. Use Create an account on the login page.");
          return;
        }
        setAppId(preferred.id);
        setAppName(preferred.name);
        setApp(preferred);
        const data = await api<MqttGuide>(`/apps/${preferred.id}/mqtt`);
        setGuide(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Load failed");
      }
    })();
  }, []);

  async function testMqtt() {
    if (!appId) return;
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const raw = await fetch(`${API_URL}/api/apps/${appId}/mqtt/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "X-App-Id": String(appId),
          "Content-Type": "application/json",
        },
      });
      const json = (await raw.json()) as {
        status?: string;
        message?: string;
        data?: MqttGuide;
      };
      if (!raw.ok) throw new Error(json.message || "Test failed");
      if (json.data) setGuide(json.data);
      setInfo(json.message || "Connection OK");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test failed");
    } finally {
      setBusy(false);
    }
  }

  async function copyText(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      setError("Could not copy");
    }
  }

  const fields = guide?.pbx_fields;
  const server = guide?.server;
  const vpsIp = server?.vps_ip || fields?.host || "—";

  return (
    <div className="users-page">
      <div className="user-toolbar">
        <div>
          <h1 className="top-oneline">MQTT / PBX</h1>
          <p className="muted">
            Connection values for <b>{appName || org?.name || "your app"}</b>. New businesses are
            created from the login page (<b>Create an account</b>) — each gets a unique Token and
            Client ID.
            {guide?.connection_status
              ? ` · ${guide.connection_status}${
                  guide.last_seen_at
                    ? ` · last seen ${new Date(guide.last_seen_at).toLocaleString()}`
                    : ""
                }`
              : ""}
          </p>
        </div>
        {isAdmin && appId ? (
          <div className="toolbar-actions">
            <button className="btn" type="button" disabled={busy} onClick={() => void testMqtt()}>
              Test connection
            </button>
          </div>
        ) : null}
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {info ? <p className="muted">{info}</p> : null}

      <section className="hint-panel">
        <p>
          Open your in-house Neron <b>System → API</b>, turn MQTT on, and paste the fields below.
          Token, Client ID, Username and Password are unique to this app — do not reuse
          another business’s values.
        </p>
      </section>

      <section className="people-table" style={{ marginTop: 12 }}>
        <div className="kv-head">
          <span>Server</span>
          <span>Value</span>
        </div>
        <div className="kv-row">
          <span>VPS server IP</span>
          <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <code className="token-value">{vpsIp}</code>
            {vpsIp !== "—" ? (
              <button type="button" className="btn ghost" onClick={() => void copyText("VPS IP", vpsIp)}>
                {copied === "VPS IP" ? "Copied" : "Copy"}
              </button>
            ) : null}
          </span>
        </div>
        {(server?.mqtt_hostname || fields?.host_hostname) && (
          <div className="kv-row">
            <span>Hostname</span>
            <span>
              <code className="token-value">{server?.mqtt_hostname || fields?.host_hostname}</code>
            </span>
          </div>
        )}
      </section>

      <section className="people-table" style={{ marginTop: 16 }}>
        <div className="kv-head">
          <span>Enter on PBX (System → API)</span>
          <span>Value</span>
        </div>
        {fields ? (
          PBX_FIELDS.filter(([key]) => key !== "host_hostname" || Boolean(fields.host_hostname)).map(
            ([key, label]) => (
              <div className="kv-row" key={key}>
                <span>
                  {label}
                  {key === "client_id" ||
                  key === "token" ||
                  key === "username" ||
                  key === "password" ? (
                    <span className="muted"> · unique to this app</span>
                  ) : null}
                </span>
                <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <code className="token-value">{fields[key] || "—"}</code>
                  {fields[key] ? (
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => void copyText(label, String(fields[key]))}
                    >
                      {copied === label ? "Copied" : "Copy"}
                    </button>
                  ) : null}
                </span>
              </div>
            ),
          )
        ) : (
          <p className="muted" style={{ padding: 12 }}>
            Loading…
          </p>
        )}
      </section>
    </div>
  );
}
